import { useCallback, useEffect, useRef, useState } from "react";
import { BATTERY_LOG_FW_MIN, parseBatteryLogLines } from "utils/batteryLog";
import { fwAtLeast } from "utils/wearableStatus";

// `battery` in `ui on` mode (firmware v0.17.0): prose first, then
// {"t":"batt_log",…}, one {"t":"batt",…} per reading, {"t":"batt_end",…}.
// The idle timeout re-arms on every line, so a 4096-reading dump is fine.
export const BATTERY_LOG_CMD = {
  command: "battery",
  startMarker: '"t":"batt_log"',
  endMarker: '"t":"batt_end"',
  includeStartMarker: true,
  timeout: 8000,
  // Older firmware answers with prose only: no header within this → too old.
  maxWaitForStart: 6000,
};

const IDLE = { status: "idle", readings: [], header: null, complete: false };

/**
 * Read the wearable's battery log over USB. `executeCommand` is
 * useSerialCommand's; `fw` the connected firmware (e.g. "v0.17.0") or null
 * while unknown. Returns { status, readings, header, complete, error, load,
 * reset } where status is idle | loading | ready | needsFirmware | error.
 */
export function useBatteryLog({ executeCommand, fw, connected }) {
  const [state, setState] = useState(IDLE);
  const alive = useRef(true);
  // Set on mount too: StrictMode (dev) mounts, unmounts and mounts again, and
  // a flag only cleared would drop every result as "unmounted".
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // A new device (or none) — forget the last one's log.
  useEffect(() => {
    if (!connected) setState(IDLE);
  }, [connected]);

  const load = useCallback(async () => {
    // Known-old firmware: don't send anything, just say so.
    if (fw && !fwAtLeast(fw, BATTERY_LOG_FW_MIN)) {
      setState({ ...IDLE, status: "needsFirmware" });
      return;
    }
    setState((prev) => ({ ...prev, status: "loading", error: null }));
    try {
      const res = await executeCommand(BATTERY_LOG_CMD);
      if (!alive.current) return;
      // executeCommand strips the batt_end line; it resolved without
      // timedOut only because that line arrived.
      const parsed = parseBatteryLogLines(res?.lines, {
        ended: !res?.timedOut,
      });
      if (!parsed.found) {
        setState({ ...IDLE, status: "needsFirmware" });
        return;
      }
      setState({
        status: "ready",
        readings: parsed.readings,
        header: parsed.header,
        complete: parsed.complete,
        error: null,
      });
    } catch (err) {
      if (!alive.current) return;
      if (err?.code === "NO_START" || err?.code === "NO_RESPONSE") {
        setState({ ...IDLE, status: "needsFirmware" });
        return;
      }
      setState((prev) => ({
        ...prev,
        status: "error",
        error: err?.message || String(err),
      }));
    }
  }, [executeCommand, fw]);

  const reset = useCallback(() => setState(IDLE), []);

  return { ...state, load, reset };
}
